import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import CloudProviderMark from "../../../../components/Common/CloudProviderMark";
import { getComplianceIcon } from "../../../../assets/images/compliance/IconCompliance.jsx";
import { AUDIT_STATUS_LABEL, AUDIT_STATUSES } from "../constants";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK } from "../theme";
import { scorePercent } from "../utils";

const STATUS_SX = {
  [AUDIT_STATUSES.DRAFT]: { bg: "#fffbeb", fg: "#b45309" },
  [AUDIT_STATUSES.ACTIVE]: { bg: "#ecfdf5", fg: "#047857" },
  [AUDIT_STATUSES.COMPLETED]: { bg: "#eff6ff", fg: "#2563eb" },
  [AUDIT_STATUSES.CANCELLED]: { bg: "#f1f5f9", fg: "#475569" },
};

export const AuditStatusChip = ({ status }) => {
  const tone = STATUS_SX[status] || STATUS_SX[AUDIT_STATUSES.CANCELLED];
  return (
    <Chip
      label={AUDIT_STATUS_LABEL[status] || status}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: tone.bg,
        color: tone.fg,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

export const FrameworkChip = ({ name }) => {
  const icon = getComplianceIcon(name);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
      }}
    >
      {icon ? (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ height: 16, width: "auto", maxWidth: 22, objectFit: "contain" }}
        />
      ) : null}
      <Typography
        noWrap
        sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#2563eb" }}
      >
        {name}
      </Typography>
    </Box>
  );
};

export const AccountCell = ({ name, cloudType }) => (
  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.5 }}>
    <CloudProviderMark
      provider={cloudType}
      sx={{
        mr: 0,
        "& .cloud-provider-mark--aws": { height: 14, width: 26 },
        "& .cloud-provider-mark--azure": { height: 16, width: 16 },
      }}
    />
    <Typography noWrap sx={{ fontSize: "0.8125rem", color: AH_TEXT }}>
      {name || "—"}
    </Typography>
  </Box>
);

export const PersonCell = ({ name, wrap = false }) => {
  const text = (
    <Typography
      noWrap={!wrap}
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        color: "#334155",
        display: "block",
        maxWidth: "100%",
        ...(wrap
          ? { whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }
          : null),
      }}
    >
      {name || "—"}
    </Typography>
  );
  if (wrap) return text;
  return <Tooltip title={name || ""}>{text}</Tooltip>;
};

export const ReadinessBar = ({ value }) => {
  const pct = scorePercent(value);
  if (pct == null) {
    return (
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#334155" }}>
        Not yet scored
      </Typography>
    );
  }
  const shown = Math.round(pct);
  const color = shown >= 80 ? "#16a34a" : shown >= 50 ? "#d97706" : shown > 0 ? "#2563eb" : "#cbd5e1";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 99,
          backgroundColor: AH_TRACK,
          overflow: "hidden",
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: `${shown}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: 99,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#334155", minWidth: 36 }}>
        {shown}%
      </Typography>
    </Box>
  );
};

export const ManualProgress = ({ decided, total }) => {
  if (total == null) {
    return (
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#334155" }}>—</Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#334155" }}>
      {decided || 0}/{total} decided
    </Typography>
  );
};

export const HeaderTip = ({ label, title }) => (
  <Box
    sx={{
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 0.5,
      lineHeight: 1.25,
    }}
  >
    <span>{label}</span>
    {title ? (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: `1px solid ${AH_BORDER}`,
            color: AH_MUTED,
            fontSize: 9,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            cursor: "default",
          }}
        >
          i
        </Box>
      </Tooltip>
    ) : null}
  </Box>
);
